import React, { useMemo } from 'react';
import {
  Box, Button, TextField, MenuItem, Stack, IconButton, Tooltip, Chip
} from '@mui/material';
import { DataGrid } from '../common/datagrid/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import EmpSelectbox from '../common/form/autocomplete/EmpSelectbox';
import { LEAVE_TYPE_LABELS, LEAVE_TYPE_COLORS } from '../../containers/attendancemng/AttendMngContainer';

const getYmOptions = () => {
  const options = [];
  const now = dayjs();
  for (let y = now.year(); y >= now.year() - 1; y--) {
    for (let m = 12; m >= 1; m--) {
      if (y === now.year() && m > now.month() + 1) continue;
      options.push(`${y}${String(m).padStart(2, '0')}`);
    }
  }
  return options;
};

const LeaveListTab = ({
  list, loading,
  searchYm, searchUsrUuid,
  onSearchYmChange, onSearchUsrChange,
  onAdd, onEdit, onDelete,
}) => {
  const searchFormik = useMemo(() => ({
    values: { searchUsr: searchUsrUuid },
    touched: {},
    errors: {},
    setFieldValue: (_name, val) => onSearchUsrChange(val ?? ''),
  }), [searchUsrUuid, onSearchUsrChange]);

  const columns = [
    { field: 'usrNm', headerName: '직원명', width: 100 },
    { field: 'dptNm', headerName: '부서', width: 120 },
    {
      field: 'leaveTyp', headerName: '구분', width: 130,
      renderCell: ({ value }) => (
        <Chip
          label={LEAVE_TYPE_LABELS[value] || value}
          color={LEAVE_TYPE_COLORS[value] || 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'leaveDtSt', headerName: '시작일', width: 110,
      valueFormatter: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
      field: 'leaveDtEd', headerName: '종료일', width: 110,
      valueFormatter: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-',
    },
    {
      field: 'leaveDays', headerName: '일수', width: 80, align: 'center', headerAlign: 'center',
      valueFormatter: (value) => value != null ? `${value}일` : '-',
    },
    {
      field: 'leaveHours', headerName: '시간', width: 80, align: 'center', headerAlign: 'center',
      valueFormatter: (value) => value != null ? `${value}h` : '-',
    },
    { field: 'rmk', headerName: '사유', flex: 1 },
    {
      field: 'actions', headerName: '', width: 90, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="수정">
            <IconButton size="small" onClick={() => onEdit(row)}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="삭제">
            <IconButton size="small" color="error" onClick={() => onDelete(row.leaveUuid)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          select size="small" label="년월" value={searchYm}
          onChange={(e) => onSearchYmChange(e.target.value)}
          sx={{ width: 140 }}
        >
          {getYmOptions().map((ym) => (
            <MenuItem key={ym} value={ym}>
              {ym.slice(0, 4)}년 {parseInt(ym.slice(4), 10)}월
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ width: 220 }}>
          <EmpSelectbox
            formik={searchFormik}
            name="searchUsr"
            label="직원 선택 (전체)"
            variant="outlined"
            margin="none"
          />
        </Box>

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained" size="small" startIcon={<AddIcon />}
          onClick={onAdd}
        >
          확정 휴가/근태 등록
        </Button>
      </Stack>

      <DataGrid
        rows={list}
        columns={columns}
        getRowId={(row) => row.leaveUuid}
        loading={loading}
        autoHeight
        density="compact"
        disableRowSelectionOnClick
        sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
        initialState={{
          sorting: { sortModel: [{ field: 'leaveDtSt', sort: 'desc' }] },
        }}
      />
    </Box>
  );
};

export default LeaveListTab;
